import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, X, FileText, Video as VideoIcon } from "lucide-react";
import { api } from "@/lib/api";

export function SearchDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ articles: [], videos: [] });
  const [loading, setLoading] = useState(false);

  const run = useCallback(async (value) => {
    if (!value.trim()) {
      setRes({ articles: [], videos: [] });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/public/search", { params: { q: value } });
      setRes(data);
    } catch {
      setRes({ articles: [], videos: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => run(q), 250);
    return () => clearTimeout(t);
  }, [q, run]);

  useEffect(() => {
    if (open) setQ("");
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const hasResults = res.articles.length > 0 || res.videos.length > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-ink/50 px-4 pt-24 backdrop-blur-sm" data-testid="search-overlay">
      <div className="w-full max-w-2xl bg-cream-100 shadow-[0_8px_40px_rgba(26,37,48,0.22)]">
        <div className="flex items-center gap-3 border-b border-gold-500/30 px-5 py-4">
          <Search className="h-5 w-5 text-steel-600" />
          <input
            autoFocus
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles and videos…"
            className="w-full bg-transparent font-sans text-lg text-ink placeholder:text-steel-400 focus:outline-none"
          />
          <button onClick={onClose} data-testid="search-close" aria-label="Close search" className="text-steel-500 hover:text-ink">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto px-5 py-4">
          {loading && <p className="py-6 text-center font-sans text-sm text-steel-500">Searching…</p>}
          {!loading && q && !hasResults && (
            <p className="py-6 text-center font-sans text-sm text-steel-500">No results found for “{q}”.</p>
          )}
          {res.articles.length > 0 && (
            <div className="mb-4">
              <p className="mb-2 font-sans text-xs uppercase tracking-[0.2em] text-gold-600">Articles</p>
              {res.articles.map((a) => (
                <Link key={a.id} to={`/articles/${a.slug}`} onClick={onClose} className="flex items-start gap-3 rounded-sm px-2 py-2 hover:bg-cream-200" data-testid={`search-result-article-${a.slug}`}>
                  <FileText className="mt-1 h-4 w-4 shrink-0 text-steel-500" />
                  <span>
                    <span className="block font-serif text-lg text-ink">{a.title}</span>
                    <span className="block font-sans text-sm text-steel-500 line-clamp-1">{a.excerpt}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          {res.videos.length > 0 && (
            <div>
              <p className="mb-2 font-sans text-xs uppercase tracking-[0.2em] text-gold-600">Videos</p>
              {res.videos.map((v) => (
                <Link key={v.id} to={`/videos/${v.slug}`} onClick={onClose} className="flex items-start gap-3 rounded-sm px-2 py-2 hover:bg-cream-200" data-testid={`search-result-video-${v.slug}`}>
                  <VideoIcon className="mt-1 h-4 w-4 shrink-0 text-steel-500" />
                  <span>
                    <span className="block font-serif text-lg text-ink">{v.title}</span>
                    <span className="block font-sans text-sm text-steel-500 line-clamp-1">{v.description}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
